/*
 *  TTTech nerve-management-system
 *  Copyright(c) 2022. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */

import axios from 'axios';
import { timeFormatHourMinSeconds } from '@/helpers/shared';
import config from '../../config/default.json';

axios.defaults.baseURL = config.BASE_URL;

let utilsStore = null;
let authStore = null;
let mqttInstance = null;

// Function that is called from main.js to set utils and auth store in this file, so that they can be used in interceptors
// avoid circular dependency by not importing pinia stores directly
export const setupAxiosInterceptors = (injectedUtils, injectedAuth, injectedMqtt) => {
  utilsStore = injectedUtils;
  authStore = injectedAuth;
  mqttInstance = injectedMqtt;
};

function setSessionHeader(request) {
  const sessionFromLS = localStorage.getItem('session');
  if (sessionFromLS) {
    const session = JSON.parse(sessionFromLS);
    if (!request.headers) {
      request.headers = {};
    }
    request.headers.sessionId = session.sessionId;
  }
}

function showToast(params) {
  utilsStore.showCustomToast(params);
}

async function getErrorCode(error) {
  const errorData = error?.response?.data;
  // handle also blob errors, used when downloading some files,
  // where the error message is in the blob text
  if (errorData instanceof Blob) {
    const text = await errorData.text();
    return JSON.parse(text)?.errorCode;
  } else {
    return errorData?.[0]?.errorCode;
  }
}

const serviceUnavailableMap = {
  svc_0001: 'Redis',
  svc_0002: 'MongoDB',
  svc_0005: 'RabbitMQ',
};

async function handleAxiosError(error) {
  const errorCode = await getErrorCode(error);
  const txt = errorCode ? `errorMessages.${errorCode}` : 'errorMessages.nerve_unexp_server_001';

  if (error?.response?.status === 429 && error?.response?.headers['content-type'].includes('text/html')) {
    showToast({ text: 'rateLimit.tooManyRequests', color: 'red', showClose: true });
    return Promise.reject(error);
  }

  // Check if the error is associated with a request that has ignoreInterceptor
  // If it does, throw error without showing toast messages
  if (error.config && error.config.ignoreInterceptor) return Promise.reject(error);

  if (error?.response?.status === 401 || error?.response?.status === 403) {
    if (error?.request?.responseURL.includes('auth/logout') || error?.request?.responseURL.includes('auth/login')) {
      if (errorCode === '00013') {
        showToast({ text: txt, color: 'red', showClose: true });
      }
      return Promise.reject(error);
    }
    // already logged out locally, avoid re-triggering the logout cascade for every remaining in-flight request
    if (authStore && !authStore.isLoggedIn()) return Promise.reject(error);
    // session no longer valid server-side, must take priority over other error branches below
    window.dispatchEvent(new CustomEvent('logout-required'));
    showToast({ text: 'errorMessages.session_expired', color: 'red', showClose: true, permanent: true });
    return Promise.reject(error);
  }

  if (error.response && error.response.data && error.response.data.error) {
    showToast({ text: error.response.data.error, color: 'red', showClose: true });
    return Promise.reject(error);
  }

  if (serviceUnavailableMap[errorCode]) {
    authStore.setServiceUnavailable({ service: serviceUnavailableMap[errorCode], isUnavailable: true });
  }

  // Compose validation errors (no toast)
  if (
    error.request &&
    error.request.responseURL.includes('/nerve/v3/workloads/compose') &&
    [
      'nerve_workload_166',
      'nerve_workload_195',
      'nerve_workload_201',
      'nerve_workload_202',
      'nerve_workload_203',
      'nerve_workload_204',
      'nerve_workload_205',
    ].includes(errorCode)
  ) {
    return Promise.reject(error);
  }

  if (errorCode === 'nerve_auth_001') {
    showToast({
      text: txt,
      textParameters: timeFormatHourMinSeconds(error.response.data[0].content?.retryAfter || 1800),
      color: 'red',
      showClose: true,
    });
    return Promise.reject(error);
  }

  if (errorCode === 'nerve_workload_218') {
    showToast({
      text: txt,
      textParameters: error.response.data[0].content?.serialNumbers,
      color: 'red',
      showClose: true,
    });
    return Promise.reject(error);
  }

  if (errorCode === 'nerve_node_106') {
    showToast({ text: txt, color: 'primary', showClose: true });
    return Promise.reject(error);
  }

  if (
    error.request &&
    error.request.responseURL.includes('docker-resources/volumes') &&
    (error.request.responseURL.includes('/import') || error.request.responseURL.includes('/export'))
  ) {
    showToast({ text: txt, color: 'red', showClose: true, permanent: true });
    return Promise.reject(error);
  }

  if (error.response) {
    showToast({ text: txt, color: 'red', showClose: true });
  }

  if (!error.response && error.message) {
    if (!utilsStore.noConnectionStatus) {
      if (error.message.toLowerCase().indexOf('network error') !== -1) {
        showToast({ text: 'errorMessages.network_error', color: 'red', showClose: true });
      }
      utilsStore.changeNoConnectionStatus(true);
    }
  }

  return Promise.reject(error);
}

axios.interceptors.request.use(
  (request) => {
    if (request && request.authRequired) {
      setSessionHeader(request);
    }
    return request;
  },
  (error) => Promise.reject(error),
);

axios.interceptors.response.use((response) => {
  if (utilsStore && utilsStore.noConnectionStatus) {
    utilsStore.changeNoConnectionStatus(false);
  }
  // Check MQTT connection and clear service unavailable if connected
  if (mqttInstance?.isConnected?.() && authStore) {
    authStore.setServiceUnavailable({ isUnavailable: false });
  }
  return response;
}, handleAxiosError);

export default axios;
